import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, API, errMsg, fmtDate, STATUSES } from "@/lib/api";
import { getTemplate, getFont } from "@/lib/poster";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminInstagram } from "@/components/AdminInstagram";
import { AdminShopify } from "@/components/AdminShopify";

const KEY = "atelier_admin_key";
const FILE_LABELS = {
  pdf: "PDF d'impression",
  cutout_groom: "Marié détouré", cutout_bride: "Mariée détourée",
  original_groom: "Marié original", original_bride: "Mariée originale",
  cutout: "Photo détourée", original: "Photo originale",
};

const Login = ({ onOk }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/login", {}, { headers: { "X-Admin-Key": pw } });
      sessionStorage.setItem(KEY, pw);
      onOk(pw);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="max-w-sm space-y-4" data-testid="admin-login-form">
      <Input type="password" required placeholder="Mot de passe atelier" value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 bg-white" data-testid="admin-password-input" />
      <button className="btn-gold" data-testid="admin-login-button">Entrer</button>
    </form>
  );
};

const Spec = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 text-sm py-1 border-b border-[#EADFD3]"><span className="text-[#9A8C7D]">{k}</span><span className={mono ? "font-mono" : ""}>{v || "—"}</span></div>
);

const OrderCard = ({ o, adminKey, onUpdate }) => {
  const [carrier, setCarrier] = useState(o.carrier_tracking);
  const file = (kind, dl) => `${API}/admin/orders/${o.id}/files/${kind}?key=${encodeURIComponent(adminKey)}${dl ? "&download=1" : ""}`;
  const c = { fonts: {}, texts: {}, ...o.config };
  return (
    <div className="bg-white rounded-2xl border border-[#DDD3C6] p-6 grid md:grid-cols-12 gap-6" data-testid={`admin-order-${o.order_number}`}>
      <img src={file("preview")} alt="" className="md:col-span-2 w-full rounded shadow-md" />
      <div className="md:col-span-5 space-y-1">
        <p className="font-mono tracking-widest">{o.order_number} {o.source === "shopify" && <span className="ml-2 text-[10px] font-sans tracking-wider uppercase bg-[#EADFD3] text-[#533C25] rounded-full px-2 py-0.5" data-testid={`admin-source-shopify-${o.order_number}`}>Shopify</span>}</p>
        <p className="text-sm text-[#6B5F53]">{o.customer_name} · {o.customer_email}</p>
        {o.customer_phone && <p className="text-sm text-[#6B5F53]" data-testid={`admin-phone-${o.order_number}`}>Téléphone : <a href={`tel:${o.customer_phone}`} className="text-[#7B5338] hover:underline" data-testid={`admin-phone-link-${o.order_number}`}>{o.customer_phone}</a></p>}
        <p className="text-xs text-[#9A8C7D] mb-3">{fmtDate(o.created_at)} · {o.size_label} · {o.price} €</p>
        {o.event_date && <p className="text-sm text-[#533C25] mb-3" data-testid={`admin-event-date-${o.order_number}`}>Événement : <strong>{o.event_date.split("-").reverse().join("/")}</strong></p>}
        <Spec k="Modèle / disposition" v={`${getTemplate(c.templateId).name} / ${getTemplate(c.templateId).layouts[c.layoutId]?.label || "—"}`} />
        <Spec k="Fond / écritures" v={`${c.bgColor || "—"} / ${c.textColor || "—"}`} mono />
        <Spec k="Photos" v={`${c.photoMode === "bw" ? "Noir & blanc" : "Couleur"} · ${c.photoOrder === "bride-left" ? "mariée à gauche" : "marié à gauche"}`} />
        <Spec k="Polices" v={Object.entries(c.fonts).map(([g, f]) => `${g}: ${getFont(f).name}`).join(", ")} />
        <Spec k="Bismillah" v={c.bismillah?.enabled ? `${c.bismillah.position} · ${c.bismillah.color}` : "non"} mono />
        <Spec k="Prénoms" v={c.texts.names} />
        <Spec k="Bienvenue" v={c.texts.welcome} />
        <Spec k="Citation" v={[c.texts.quote, c.texts.reference && `(${c.texts.reference})`].filter(Boolean).join(" ")} />
        <Spec k="Phrase libre" v={c.texts.extra} />
      </div>
      <div className="md:col-span-5 space-y-3">
        <Select value={o.status} onValueChange={(v) => onUpdate(o.id, { status: v })}>
          <SelectTrigger className="h-11" data-testid={`admin-status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`admin-status-option-${s.key}`}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input placeholder="N° suivi transporteur" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="h-11" data-testid={`admin-carrier-input-${o.order_number}`} />
          <button className="btn-outline text-sm" onClick={() => onUpdate(o.id, { carrier_tracking: carrier })} data-testid={`admin-carrier-save-${o.order_number}`}>OK</button>
        </div>
        <a href={file("print", true)} className="btn-gold w-full justify-center text-sm" data-testid={`admin-download-print-${o.order_number}`}><Download size={14} /> Fichier d'impression HD (150 dpi)</a>
        <div className="grid grid-cols-2 gap-2">
          {o.files.filter((k) => FILE_LABELS[k]).map((k) => (
            <a key={k} href={file(k, true)} className="btn-outline text-xs justify-center" data-testid={`admin-download-${k}-${o.order_number}`}>{FILE_LABELS[k]}</a>
          ))}
        </div>
        <a href={`/suivi/${o.tracking_token}`} target="_blank" rel="noreferrer" className="text-xs text-[#9A8C7D] underline">Lien de suivi client</a>
      </div>
    </div>
  );
};

export default function Admin() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY) || "");
  const [orders, setOrders] = useState([]);
  const headers = { "X-Admin-Key": adminKey };

  const load = useCallback(() => {
    http.get("/admin/orders", { headers: { "X-Admin-Key": adminKey } }).then((r) => setOrders(r.data)).catch(() => { sessionStorage.removeItem(KEY); setAdminKey(""); });
  }, [adminKey]);
  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const update = async (id, body) => {
    try {
      const { data } = await http.patch(`/admin/orders/${id}`, body, { headers });
      setOrders((os) => os.map((o) => (o.id === id ? data : o)));
      toast.success("Commande mise à jour");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 space-y-10">
      <div className="flex justify-between items-end">
        <div><span className="label-xs">Espace atelier</span><h1 className="font-display text-5xl mt-3">Commandes & fichiers d'impression</h1></div>
        {adminKey && <div className="flex gap-2">
          <button className="btn-outline text-sm" onClick={load} data-testid="admin-refresh-button"><RefreshCw size={14} /></button>
          <button className="btn-outline text-sm" onClick={() => { sessionStorage.removeItem(KEY); setAdminKey(""); }} data-testid="admin-logout-button"><LogOut size={14} /></button>
        </div>}
      </div>
      {!adminKey ? <Login onOk={setAdminKey} /> : (
        <Tabs defaultValue="orders">
          <TabsList className="mb-6">
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Commandes</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avis & photos</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="admin-tab-instagram">Instagram</TabsTrigger>
            <TabsTrigger value="shopify" data-testid="admin-tab-shopify">Shopify</TabsTrigger>
          </TabsList>
          <TabsContent value="orders">
            <div className="space-y-6" data-testid="admin-orders-list">
              {orders.length === 0 && <p className="text-[#9A8C7D]" data-testid="admin-no-orders">Aucune commande pour le moment.</p>}
              {orders.map((o) => <OrderCard key={o.id} o={o} adminKey={adminKey} onUpdate={update} />)}
            </div>
          </TabsContent>
          <TabsContent value="reviews"><AdminReviews adminKey={adminKey} /></TabsContent>
          <TabsContent value="instagram"><AdminInstagram adminKey={adminKey} /></TabsContent>
          <TabsContent value="shopify"><AdminShopify adminKey={adminKey} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
